import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import { AlertTriangle, Download, Loader2, Package, RefreshCw, Zap, Info, Landmark, Ban } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Riusabile: mostra anteprima split (doppio massimale istantanei/ordinari) e gestisce download ZIP.
 *
 * Props:
 *  - sourceFile: File (per Sabadell/Booking) o null (per fatture)
 *  - planEndpoint: string es. '/api/admin/sepa-split/sabadell-plan'
 *  - downloadEndpoint: string es. '/api/admin/sepa-split/sabadell-download'
 *  - extraFormFields: { stay_date_from, stay_date_to, ... } per file upload endpoints
 *  - jsonBody: object usato al posto di multipart per endpoint invoice JSON
 *  - filenamePrefix: 'bonifici_banca_asti' / 'commissioni_booking' / 'pagamento_fatture'
 *  - onDownloaded: callback dopo download riuscito
 *  - title: titolo del pannello
 */
export default function SepaSplitPanel({
  sourceFile,
  planEndpoint,
  downloadEndpoint,
  extraFormFields = {},
  jsonBody = null,
  filenamePrefix = 'SEPA',
  onDownloaded,
  title = 'Split SEPA — Limiti Banca di Asti',
}) {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isStale, setIsStale] = useState(false);
  const [planData, setPlanData] = useState(null);
  const [editedPlan, setEditedPlan] = useState([]);
  const [excludedBatches, setExcludedBatches] = useState(() => new Set());
  // Escludi automaticamente dal piano i bonifici già presenti in lotti generati
  const [excludePaid, setExcludePaid] = useState(true);
  // Modalità: 'mixed' = 1 istantaneo + resto ordinari | 'instant' | 'ordinary'
  const [transferMode, setTransferMode] = useState('mixed');
  // Limiti effettivi personalizzabili (prefill da config server)
  const [instantLimit, setInstantLimit] = useState('');
  const [ordinaryLimit, setOrdinaryLimit] = useState('');
  const limitsEdited = useRef(false);
  const [usageData, setUsageData] = useState(null);
  const [error, setError] = useState('');
  const token = localStorage.getItem('hw_token');

  const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  const fetchPlan = async (mode = transferMode, opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const iLim = opts.instantLimit !== undefined ? opts.instantLimit : instantLimit;
      const oLim = opts.ordinaryLimit !== undefined ? opts.ordinaryLimit : ordinaryLimit;
      const sendLimits = limitsEdited.current && parseFloat(iLim) > 0 && parseFloat(oLim) > 0;
      let res;
      if (jsonBody) {
        res = await fetch(`${API_URL}${planEndpoint}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...jsonBody,
            transfer_mode: mode,
            exclude_already_paid: excludePaid,
            ...(sendLimits ? { instant_limit_eur: parseFloat(iLim), ordinary_limit_eur: parseFloat(oLim) } : {}),
          }),
        });
      } else if (sourceFile) {
        const formData = new FormData();
        formData.append('file', sourceFile);
        formData.append('transfer_mode', mode);
        formData.append('exclude_already_paid', excludePaid ? 'true' : 'false');
        if (sendLimits) {
          formData.append('instant_limit_eur', String(parseFloat(iLim)));
          formData.append('ordinary_limit_eur', String(parseFloat(oLim)));
        }
        res = await fetch(`${API_URL}${planEndpoint}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      } else {
        throw new Error('Sorgente file mancante');
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore HTTP ${res.status}`);
      }
      const data = await res.json();
      setPlanData(data);
      if (!limitsEdited.current) {
        if (data.instant_effective_eur) setInstantLimit(String(data.instant_effective_eur));
        if (data.ordinary_effective_eur) setOrdinaryLimit(String(data.ordinary_effective_eur));
      }
      const edited = (data.plan || []).map((b) => ({
        batch_index: b.batch_index,
        execution_date: b.execution_date,
        transfer_type: b.transfer_type,
        transactions: b.transactions,
        total: b.total,
        count: b.count,
        beneficiaries_count: b.beneficiaries_count,
        oldest_check_in: b.oldest_check_in,
      }));
      setEditedPlan(edited);
      setExcludedBatches(new Set());
      setIsStale(false);
    } catch (e) {
      setError(e.message);
      setPlanData(null);
      setEditedPlan([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sourceFile || jsonBody) {
      setIsStale(true);
      const timeoutId = setTimeout(() => {
        fetchPlan();
      }, 350);
      return () => clearTimeout(timeoutId);
    }
  }, [sourceFile, JSON.stringify(jsonBody), excludePaid]);

  // ─── Massimale residuo: quanto è già stato generato per le date del piano ───
  const planDates = editedPlan.map((b) => (b.execution_date || '').slice(0, 10)).filter(Boolean).sort();
  useEffect(() => {
    if (!planDates.length) { setUsageData(null); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/sepa-split/daily-usage?date_from=${planDates[0]}&date_to=${planDates[planDates.length - 1]}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsageData(r.ok ? await r.json() : null);
      } catch (_) { setUsageData(null); }
    })();
  }, [JSON.stringify(planDates)]); // eslint-disable-line

  const updateBatchDate = (idx, newDate) => {
    setEditedPlan((prev) => prev.map((b, i) => (i === idx ? { ...b, execution_date: newDate } : b)));
  };

  const toggleBatchIncluded = (batchIndex) => {
    setExcludedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batchIndex)) next.delete(batchIndex);
      else next.add(batchIndex);
      return next;
    });
  };

  const includedPlan = editedPlan.filter((b) => !excludedBatches.has(b.batch_index));
  const includedCount = includedPlan.length;
  const includedTxCount = includedPlan.reduce((s, b) => s + b.count, 0);
  const includedTotal = includedPlan.reduce((s, b) => s + b.total, 0);
  const instantTotal = includedPlan.filter((b) => b.transfer_type === 'instant').reduce((s, b) => s + b.total, 0);
  const ordinaryTotal = includedPlan.filter((b) => b.transfer_type !== 'instant').reduce((s, b) => s + b.total, 0);

  // ─── Avviso massimale residuo: lotti che superano il residuo giornaliero (usato + cumulato nel piano) ───
  const overflowWarnings = (() => {
    if (!usageData) return [];
    const cum = {};
    const out = [];
    includedPlan.forEach((b) => {
      if (!b.transfer_type) return;
      const day = (b.execution_date || '').slice(0, 10);
      const u = usageData.usage?.[day] || {};
      const eff = b.transfer_type === 'instant' ? usageData.instant_effective_eur : usageData.ordinary_effective_eur;
      const used = (b.transfer_type === 'instant' ? (u.instant_used || 0) : (u.ordinary_used || 0)) + (u.unknown_used || 0);
      const key = `${day}|${b.transfer_type}`;
      const prev = cum[key] || 0;
      const remaining = eff - used - prev;
      if (b.total > remaining + 0.01) {
        out.push({ batch_index: b.batch_index, transfer_type: b.transfer_type, date: day, total: b.total, remaining: Math.max(0, remaining), used });
      }
      cum[key] = prev + b.total;
    });
    return out;
  })();
  const overflowSet = new Set(overflowWarnings.map((w) => w.batch_index));
  const usedDays = Object.entries(usageData?.usage || {}).filter(([, u]) => (u.batches || 0) > 0).sort();

  const handleModeChange = (mode) => {
    setTransferMode(mode);
    fetchPlan(mode);
  };

  const handleRecalc = () => {
    const i = parseFloat(instantLimit);
    const o = parseFloat(ordinaryLimit);
    if (isNaN(i) || i <= 0 || isNaN(o) || o <= 0) {
      toast.error('Inserisci limiti validi (> 0)');
      return;
    }
    limitsEdited.current = true;
    fetchPlan(transferMode);
  };

  const handleDownload = async () => {
    if (!includedPlan.length) return;
    setDownloading(true);
    setError('');
    try {
      const planForBackend = includedPlan.map((b, i) => ({
        batch_index: i + 1,
        execution_date: b.execution_date,
        transfer_type: b.transfer_type,
        transaction_refs: b.transactions.map((tx) => ({
          reference: tx.reference,
          amount: tx.amount,
        })),
      }));

      let res;
      if (jsonBody) {
        res = await fetch(`${API_URL}${downloadEndpoint}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...jsonBody, plan: planForBackend, transfer_mode: transferMode }),
        });
      } else {
        const formData = new FormData();
        formData.append('file', sourceFile);
        formData.append('plan_json', JSON.stringify(planForBackend));
        Object.entries(extraFormFields || {}).forEach(([k, v]) => {
          if (v) formData.append(k, v);
        });
        res = await fetch(`${API_URL}${downloadEndpoint}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+?)"/);
      const filename = match ? match[1] : `${filenamePrefix}_split.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`ZIP scaricato: ${filename}`);
      if (onDownloaded) onDownloaded({ split_group_id: res.headers.get('X-Split-Group-Id'), filename });
    } catch (e) {
      setError(e.message);
      toast.error(`Errore: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 border-amber-300 bg-amber-50/40" data-testid="sepa-split-loading">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
          <span className="text-sm text-amber-800">Analisi del piano di split in corso...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-4 border-red-300 bg-red-50" data-testid="sepa-split-error">
        <div className="flex items-center gap-3 text-red-700">
          <AlertTriangle className="h-5 w-5" />
          <span className="text-sm">{error}</span>
          <Button size="sm" variant="outline" onClick={() => fetchPlan()} className="ml-auto">
            <RefreshCw className="h-3 w-3 mr-1" /> Riprova
          </Button>
        </div>
      </Card>
    );
  }

  if (!planData) return null;

  const fmtNum = (v) => Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });
  const dupList = planData.already_paid_duplicates || [];
  const alreadyPaidBanner = dupList.length > 0 ? (
    <Card className="p-3 bg-red-50 border-red-300 space-y-2" data-testid="split-already-paid-banner">
      <p className="text-sm font-bold text-red-800">
        🚨 {dupList.length} bonific{dupList.length === 1 ? 'o GIÀ PRESENTE' : 'i GIÀ PRESENTI'} in lotti generati di recente
      </p>
      {planData.already_paid_excluded ? (
        <p className="text-xs text-emerald-700 font-semibold" data-testid="split-already-paid-excluded-note">
          ✅ Esclusi automaticamente dal piano ({fmtEur(planData.excluded_already_paid_total)}): verranno generati SOLO i bonifici restanti ({fmtEur(planData.planned_amount)}).
        </p>
      ) : (
        <p className="text-xs text-red-700 font-bold" data-testid="split-already-paid-included-warning">
          ⚠️ NON esclusi: il piano li include DI NUOVO — rischio DOPPIO PAGAMENTO!
        </p>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
        <Checkbox checked={excludePaid} onCheckedChange={(v) => setExcludePaid(Boolean(v))} data-testid="split-exclude-paid-toggle" />
        Escludi automaticamente i bonifici già generati
      </label>
      <details className="text-xs text-slate-600">
        <summary className="cursor-pointer font-semibold">Vedi elenco ({dupList.length})</summary>
        <div className="mt-1 max-h-40 overflow-y-auto space-y-0.5">
          {dupList.map((d, i) => (
            <p key={i}>• {d.reference} — € {fmtNum(d.amount)} — {d.beneficiary}{' '}
              <span className="text-slate-400">(lotto {(d.previous_batch?.file || '?')} del {(d.previous_batch?.created_at || '').slice(0, 10)})</span>
            </p>
          ))}
        </div>
      </details>
    </Card>
  ) : null;

  if (!planData.needs_split) {
    return (
      <div className="space-y-3">
        {alreadyPaidBanner}
        {planData.all_already_paid ? (
          <Card className="p-4 border-red-300 bg-red-50" data-testid="sepa-split-all-paid">
            <div className="flex items-center gap-3 text-red-800">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <div className="text-sm font-semibold">
                Tutti i bonifici di questo file risultano GIÀ generati in lotti precedenti — non c'è nulla da generare.
                NON usare il bottone di conversione standard: rifarebbe l'intero file.
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-4 border-emerald-300 bg-emerald-50/50" data-testid="sepa-split-not-needed">
            <div className="flex items-center gap-3 text-emerald-800">
              <Info className="h-5 w-5" />
              <div className="text-sm">
                Totale <strong>{fmtEur(planData.total_amount)}</strong> sotto la soglia istantanei{' '}
                <strong>{fmtEur(planData.effective_limit_eur)}</strong> — usa il bottone standard per generare un singolo XML.
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  }

  const modeButtons = [
    { key: 'mixed', label: 'Misto', desc: '1 istantaneo + resto ordinari' },
    { key: 'instant', label: 'Solo istantanei', desc: `max ${fmtEur(planData.instant_effective_eur)}/giorno` },
    { key: 'ordinary', label: 'Solo ordinari', desc: `max ${fmtEur(planData.ordinary_effective_eur)}/giorno` },
  ];

  return (
    <Card className={`p-5 border-2 ${isStale ? 'border-blue-400 bg-blue-50/40' : 'border-amber-400 bg-amber-50/50'} space-y-4 transition-colors`} data-testid="sepa-split-panel">
      {isStale && (
        <div className="bg-blue-100 border border-blue-300 px-3 py-2 rounded flex items-center gap-2 text-sm text-blue-800">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Aggiornamento split in corso (selezione modificata)...</span>
        </div>
      )}
      {alreadyPaidBanner}
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h3 className="font-bold text-amber-900 text-base">{title}</h3>
          <p className="text-sm text-amber-800 mt-1">
            Totale <strong>{fmtEur(planData.total_amount)}</strong> supera la soglia istantanei{' '}
            <strong>{fmtEur(planData.instant_effective_eur)}</strong>.
            Verrà generato uno ZIP con <strong>{includedCount} file XML</strong>
            {instantTotal > 0 && <> — istantanei: <strong>{fmtEur(instantTotal)}</strong></>}
            {ordinaryTotal > 0 && <> — ordinari: <strong>{fmtEur(ordinaryTotal)}</strong></>}
            {excludedBatches.size > 0 && <> — <strong>{excludedBatches.size}</strong> lotto/i escluso/i dal download</>}.
          </p>
        </div>
      </div>

      {/* Modalità di generazione */}
      <div className="bg-white p-3 rounded-lg border border-amber-200 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide mr-1">Come vuoi generare i bonifici?</span>
          {modeButtons.map((m) => (
            <button
              key={m.key}
              onClick={() => handleModeChange(m.key)}
              className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                transferMode === m.key
                  ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                  : 'bg-white border-slate-300 text-slate-600 hover:border-blue-400'
              }`}
              data-testid={`transfer-mode-${m.key}`}
              title={m.desc}
            >
              <span className="font-medium">{m.label}</span>
              <span className={`block text-[10px] ${transferMode === m.key ? 'text-blue-100' : 'text-slate-400'}`}>{m.desc}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] font-medium text-slate-600 flex items-center gap-1">
              <Zap className="h-3 w-3 text-blue-600" /> Limite file istantaneo (€)
            </label>
            <Input
              type="number"
              step="500"
              min="0"
              value={instantLimit}
              onChange={(e) => { setInstantLimit(e.target.value); limitsEdited.current = true; }}
              className="w-[140px] h-8 mt-0.5"
              data-testid="instant-limit-input"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600 flex items-center gap-1">
              <Landmark className="h-3 w-3 text-slate-500" /> Limite file ordinario (€)
            </label>
            <Input
              type="number"
              step="500"
              min="0"
              value={ordinaryLimit}
              onChange={(e) => { setOrdinaryLimit(e.target.value); limitsEdited.current = true; }}
              className="w-[140px] h-8 mt-0.5"
              data-testid="ordinary-limit-input"
            />
          </div>
          <Button size="sm" variant="outline" onClick={handleRecalc} data-testid="recalc-plan-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Ricalcola piano
          </Button>
        </div>
      </div>

      {(usedDays.length > 0 || overflowWarnings.length > 0) && (
        <Card className={`p-3 ${overflowWarnings.length ? 'bg-red-50 border-red-400' : 'bg-sky-50 border-sky-300'}`} data-testid="daily-usage-card">
          <div className={`flex items-start gap-2 text-xs ${overflowWarnings.length ? 'text-red-900' : 'text-sky-900'}`}>
            {overflowWarnings.length ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> : <Info className="h-4 w-4 mt-0.5 shrink-0" />}
            <div className="space-y-1">
              <strong>Massimale residuo giornaliero</strong>
              {usedDays.map(([day, u]) => (
                <div key={day} data-testid={`daily-usage-${day}`}>
                  {new Date(day + 'T00:00:00').toLocaleDateString('it-IT')}: già generati{' '}
                  <strong>{fmtEur(u.instant_used)}</strong> istantanei (residuo <strong>{fmtEur(u.instant_remaining)}</strong>) ·{' '}
                  <strong>{fmtEur(u.ordinary_used)}</strong> ordinari (residuo <strong>{fmtEur(u.ordinary_remaining)}</strong>)
                  {u.unknown_used > 0 && <> · <strong>{fmtEur(u.unknown_used)}</strong> senza tipo (XML singoli: scalati prudenzialmente da entrambi i residui)</>}
                </div>
              ))}
              {overflowWarnings.map((w) => (
                <div key={w.batch_index} className="font-semibold" data-testid={`overflow-warning-${w.batch_index}`}>
                  ⚠ Lotto {w.batch_index} ({w.transfer_type === 'instant' ? 'Istantaneo' : 'Ordinario'}, {new Date(w.date + 'T00:00:00').toLocaleDateString('it-IT')}):{' '}
                  {fmtEur(w.total)} supera il residuo disponibile di {fmtEur(w.remaining)} — sposta la data o riduci il lotto.
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {(planData.excluded_names_applied || []).length > 0 && (
        <Card className="p-3 bg-violet-50 border-violet-300" data-testid="excluded-names-notice">
          <div className="flex items-start gap-2 text-violet-900 text-xs">
            <Ban className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <strong>Esclusi dai bonifici istantanei</strong> (la loro banca non li accetta):{' '}
              {planData.excluded_names_applied.join(', ')} — inseriti automaticamente nei lotti <strong>ordinari</strong>.
            </div>
          </div>
        </Card>
      )}

      {planData.duplicate_warning && (
        <Card className="p-3 bg-orange-50 border-orange-300">
          <div className="flex items-start gap-2 text-orange-900 text-xs">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <strong>Attenzione duplicato:</strong> hai già scaricato uno split di questo identico file il{' '}
              {new Date(planData.duplicate_warning.created_at).toLocaleString('it-IT')} (utente: {planData.duplicate_warning.created_by}).
              Prosegui solo se sai cosa stai facendo, altrimenti rischi di pagare due volte.
            </div>
          </div>
        </Card>
      )}

      {(planData.already_paid_duplicates || []).length > 0 && (
        <Card className="p-3 bg-red-50 border-red-500" data-testid="already-paid-duplicates-card">
          <div className="flex items-start gap-2 text-red-900 text-xs">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="space-y-1.5">
              <strong data-testid="already-paid-duplicates-title">
                🚨 {planData.already_paid_duplicates.length === 1 ? '1 bonifico GIÀ PRESENTE' : `${planData.already_paid_duplicates.length} bonifici GIÀ PRESENTI`} in lotti precedenti — rischio DOPPIO PAGAMENTO
              </strong>
              <div>
                Questi riferimenti erano già inclusi in file generati negli ultimi 60 giorni.
                Se procedi verranno pagati DI NUOVO: rigenera l'export dal gestionale escludendoli,
                oppure prosegui solo se è un ri-pagamento voluto.
              </div>
              {planData.already_paid_duplicates.map((d, i) => (
                <div key={i} className="font-mono" data-testid={`already-paid-dup-${i}`}>
                  • {d.reference} — {fmtEur(d.amount)} — {d.beneficiary} → già in{' '}
                  <strong>{d.previous_batch?.file || 'lotto precedente'}</strong>
                  {d.previous_batch?.created_at ? ` del ${new Date(d.previous_batch.created_at).toLocaleDateString('it-IT')}` : ''}
                  {' '}({d.previous_batch?.status === 'executed' ? 'GIÀ ESEGUITO in banca' : 'generato, non ancora eseguito'})
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {(planData.excluded_sepa || []).length > 0 && (
        <Card className="p-3 bg-amber-50 border-amber-400" data-testid="sepa-excluded-payments-card">
          <div className="flex items-start gap-2 text-amber-950 text-xs">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <strong>{planData.excluded_sepa.length} pagamenti esclusi dall’XML SEPA</strong>
              <p>Gli IBAN extra-SEPA devono essere gestiti separatamente; quelli non validi devono essere corretti.</p>
              {planData.excluded_sepa.map((row, index) => (
                <p key={`${row.reference}-${index}`} data-testid={`sepa-excluded-payment-${index}`}>
                  • {row.beneficiary || 'Beneficiario'}{row.reference ? ` · ${row.reference}` : ''} — {row.reason}
                </p>
              ))}
            </div>
          </div>
        </Card>
      )}

      <div className="overflow-x-auto" data-testid="split-plan-table">
        <table className="w-full text-sm">
          <thead className="bg-amber-100/60 text-amber-900">
            <tr>
              <th className="text-center px-3 py-2 font-medium" title="Includi questo lotto nel download">Includi</th>
              <th className="text-left px-3 py-2 font-medium">Lotto</th>
              <th className="text-left px-3 py-2 font-medium">Tipo</th>
              <th className="text-left px-3 py-2 font-medium">Data esecuzione</th>
              {editedPlan.some((b) => b.oldest_check_in) && (
                <th className="text-left px-3 py-2 font-medium" title="Check-in più vecchio nel lotto: priorità a chi aspetta da più tempo">In attesa dal</th>
              )}
              <th className="text-right px-3 py-2 font-medium">Transazioni</th>
              <th className="text-right px-3 py-2 font-medium">Beneficiari</th>
              <th className="text-right px-3 py-2 font-medium">Totale</th>
              <th className="text-left px-3 py-2 font-medium">% del limite</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amber-200">
            {editedPlan.map((b, idx) => {
              const isInstant = b.transfer_type === 'instant';
              const batchLimit = isInstant ? planData.instant_effective_eur : (planData.ordinary_effective_eur || planData.effective_limit_eur);
              const pct = (b.total / batchLimit) * 100;
              const d = new Date(b.execution_date);
              const dayName = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'][d.getDay()];
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              const isExcluded = excludedBatches.has(b.batch_index);
              return (
                <tr key={idx} className={`hover:bg-white transition-colors ${isExcluded ? 'bg-slate-100/70 opacity-60' : 'bg-white/60'}`} data-testid={`split-batch-row-${b.batch_index}`}>
                  <td className="px-3 py-2 text-center">
                    <Checkbox
                      checked={!isExcluded}
                      onCheckedChange={() => toggleBatchIncluded(b.batch_index)}
                      aria-label={`Includi lotto ${b.batch_index}`}
                      data-testid={`split-batch-include-${b.batch_index}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-semibold">
                    {b.batch_index}/{editedPlan.length}
                    {isExcluded && <Badge className="ml-2 bg-slate-200 text-slate-600">Escluso</Badge>}
                  </td>
                  <td className="px-3 py-2">
                    {b.transfer_type ? (
                      isInstant ? (
                        <Badge className="bg-blue-100 text-blue-700 gap-1" data-testid={`batch-type-${b.batch_index}`}>
                          <Zap className="h-3 w-3" /> Istantaneo
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-200 text-slate-700 gap-1" data-testid={`batch-type-${b.batch_index}`}>
                          <Landmark className="h-3 w-3" /> Ordinario
                        </Badge>
                      )
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={b.execution_date}
                        onChange={(e) => updateBatchDate(idx, e.target.value)}
                        className="w-[160px] h-8"
                        data-testid={`split-date-input-${b.batch_index}`}
                      />
                      <Badge className={isWeekend ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}>
                        {dayName}
                      </Badge>
                    </div>
                  </td>
                  {editedPlan.some((x) => x.oldest_check_in) && (
                    <td className="px-3 py-2 text-xs text-slate-600" data-testid={`batch-oldest-checkin-${b.batch_index}`}>
                      {b.oldest_check_in ? new Date(b.oldest_check_in + 'T00:00:00').toLocaleDateString('it-IT') : '—'}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right">{b.count}</td>
                  <td className="px-3 py-2 text-right">{b.beneficiaries_count}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    <span className="inline-flex items-center gap-1">
                      {overflowSet.has(b.batch_index) && (
                        <AlertTriangle className="h-4 w-4 text-red-500" data-testid={`overflow-icon-${b.batch_index}`} title="Supera il massimale residuo del giorno" />
                      )}
                      {fmtEur(b.total)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-slate-100 rounded h-2 overflow-hidden max-w-[100px]">
                        <div
                          className={`h-full ${pct >= 95 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-600">{pct.toFixed(0)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-amber-100/40 font-bold">
            <tr>
              <td colSpan={editedPlan.some((b) => b.oldest_check_in) ? 5 : 4} className="px-3 py-2">
                Da generare: {includedCount} di {editedPlan.length} lotti
              </td>
              <td className="px-3 py-2 text-right">{includedTxCount}</td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2 text-right font-mono">{fmtEur(includedTotal)}</td>
              <td className="px-3 py-2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="bg-white p-3 rounded border border-amber-200 text-xs text-slate-600">
        <Info className="h-3.5 w-3.5 inline mr-1 text-slate-500" />
        Carica ciascun XML in Banca di Asti <strong>il giorno della data di esecuzione</strong> indicata,
        selezionando la modalità <strong>Istantaneo</strong> o <strong>Ordinario</strong> in base al tipo del lotto
        (il nome file lo indica). Istantanei e ordinari hanno massimali giornalieri separati, quindi possono
        partire lo stesso giorno.
      </div>

      <div className="flex justify-end gap-2">
        <Button
          onClick={handleDownload}
          disabled={downloading || !includedCount}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="download-split-zip-btn"
        >
          {downloading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generazione ZIP...</>
          ) : (
            <><Package className="h-4 w-4 mr-2" /> Scarica ZIP ({includedCount} file XML)</>
          )}
        </Button>
      </div>
    </Card>
  );
}
